import { memo, useEffect, useRef, useState } from 'react'
import type { NodeProps } from '@xyflow/react'
import { Handle, Position } from '@xyflow/react'
import { ANGLE_CREDITS, angleWords } from '../../shared/services/angleService'
import { SkillGlyph } from '@shared/components/SkillGlyph'

/**
 * The Angle card (Huanrui, 2026-10-09)
 * -------------------------------------------------------------
 * One picture wired in on the left; the same thing seen from somewhere else
 * comes out on the right (routes/angles.ts: FLUX 2 through fal).
 *
 * Where from is set with the cube: drag it round and up the way you would
 * turn the object in your hand. Its front face shows the wired picture, so
 * the face turned towards you is the side the new picture looks at. The
 * buttons under it are the usual places, and how close.
 *
 * The three numbers are fal's own: round 0-360 (0 front, 90 right side,
 * 180 back, 270 left), up 0-60 (0 eye level, 60 from well above), close
 * 0-10 (0 wide, 10 close).
 */

export interface AngleNodeData extends Record<string, unknown> {
  skillRef: string
  name: string
  glyph: string
  glow: string
  open?: boolean
  horizontal?: number
  vertical?: number
  zoom?: number
  /** Runs in flight from this card, by id. */
  runs?: string[]
  error?: string
  /** When a press last ended without starting a run: clears "Starting…". */
  tried?: number
  credits?: number
  madeCount?: number

  /* handed in by the board, never stored */
  live?: number
  maxLive?: number
  /** The wired picture's small copy, for the cube's front. '' when none is wired. */
  preview?: string
  onEdit?: (patch: Partial<AngleNodeData>) => void
  onRun?: () => void
}

const PLACES: { label: string; horizontal: number; vertical: number }[] = [
  { label: 'Front', horizontal: 0, vertical: 0 },
  { label: '¾ right', horizontal: 45, vertical: 15 },
  { label: 'Right', horizontal: 90, vertical: 0 },
  { label: 'Back', horizontal: 180, vertical: 0 },
  { label: 'Left', horizontal: 270, vertical: 0 },
  { label: '¾ left', horizontal: 315, vertical: 15 },
  { label: 'Above', horizontal: 0, vertical: 60 },
]

const DISTANCES: { label: string; zoom: number }[] = [
  { label: 'Wide', zoom: 2 },
  { label: 'Medium', zoom: 5 },
  { label: 'Close', zoom: 8 },
]

const round = (value: number) => ((Math.round(value) % 360) + 360) % 360
const clampUp = (value: number) => Math.min(60, Math.max(0, Math.round(value)))

export const AngleNode = memo(function AngleNode({ data, selected }: NodeProps) {
  const card = data as AngleNodeData
  const isOpen = card.open !== false
  const mine = (card.runs ?? []).length
  const live = card.live ?? mine
  const full = live >= (card.maxLive ?? 5)
  const wired = Boolean(card.preview)
  const edit = (patch: Partial<AngleNodeData>) => card.onEdit?.(patch)

  const saved = { horizontal: round(card.horizontal ?? 0), vertical: clampUp(card.vertical ?? 0) }
  const zoom = card.zoom ?? 5

  // While dragging, the cube follows the mouse here; the card is told once, on letting go.
  const [turning, setTurning] = useState<{ horizontal: number; vertical: number } | null>(null)
  const grab = useRef<{ x: number; y: number; horizontal: number; vertical: number } | null>(null)
  const shown = turning ?? saved

  const [starting, setStarting] = useState(false)
  useEffect(() => {
    setStarting(false)
  }, [mine, card.error, card.tried])
  useEffect(() => {
    if (!starting) return
    const stop = setTimeout(() => setStarting(false), 15000)
    return () => clearTimeout(stop)
  }, [starting])

  const onDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return
    event.stopPropagation()
    event.currentTarget.setPointerCapture(event.pointerId)
    grab.current = { x: event.clientX, y: event.clientY, ...shown }
    setTurning(shown)
  }
  const onMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const from = grab.current
    if (!from) return
    // Dragging right turns the object right, so its left side comes round:
    // the camera goes the other way. Dragging down tips its top towards you.
    setTurning({
      horizontal: round(from.horizontal - (event.clientX - from.x) * 0.6),
      vertical: clampUp(from.vertical + (event.clientY - from.y) * 0.4),
    })
  }
  const onUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!grab.current) return
    grab.current = null
    try {
      event.currentTarget.releasePointerCapture(event.pointerId)
    } catch {
      /* already let go */
    }
    if (turning) edit({ horizontal: turning.horizontal, vertical: turning.vertical })
    setTurning(null)
  }

  const cube = {
    transform: `rotateX(${-shown.vertical}deg) rotateY(${-shown.horizontal}deg)`,
  } as React.CSSProperties

  return (
    <div
      className={[
        'cnode',
        'pnode',
        'anode',
        isOpen ? 'pnode--open' : '',
        selected ? 'cnode--selected' : '',
        wired ? 'cnode--lit' : '',
        mine > 0 ? 'cnode--running' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{ '--tile-glow': card.glow } as React.CSSProperties}
    >
      <Handle type="target" id="in" position={Position.Left} className="cnode__port" />
      <Handle type="source" id="out" position={Position.Right} className="cnode__port" />

      <div className="pnode__head">
        <span className="cnode__glyph" aria-hidden="true">
          <SkillGlyph glyph={card.glyph} />
        </span>
        <span className="cnode__name">{card.name}</span>
        <button
          type="button"
          className="pnode__toggle nodrag"
          onClick={() => edit({ open: !isOpen })}
          title={isOpen ? 'Fold this card' : 'Unfold this card'}
          aria-label={isOpen ? 'Fold this card' : 'Unfold this card'}
        >
          {isOpen ? '−' : '+'}
        </button>
      </div>

      {isOpen ? (
        <div className="pnode__body">
          <div
            className={`anode__stage nodrag nopan${turning ? ' anode__stage--turning' : ''}`}
            onPointerDown={onDown}
            onPointerMove={onMove}
            onPointerUp={onUp}
            onPointerCancel={onUp}
            title="Drag to turn"
          >
            <div className="anode__cube" style={cube}>
              <div className="anode__face anode__face--front">
                {card.preview ? <img src={card.preview} alt="" draggable={false} /> : <span>Front</span>}
              </div>
              <div className="anode__face anode__face--right"><span>Right</span></div>
              <div className="anode__face anode__face--back"><span>Back</span></div>
              <div className="anode__face anode__face--left"><span>Left</span></div>
              <div className="anode__face anode__face--top"><span>Top</span></div>
              <div className="anode__face anode__face--bottom"><span>Bottom</span></div>
            </div>
            <span className="anode__read">{angleWords(shown.horizontal, shown.vertical, zoom)}</span>
          </div>

          <div className="pnode__chips pnode__chips--grid anode__places">
            {PLACES.map((one) => (
              <button
                key={one.label}
                type="button"
                className={`pnode__chip nodrag${
                  one.horizontal === saved.horizontal && one.vertical === saved.vertical ? ' pnode__chip--on' : ''
                }`}
                onClick={() => edit({ horizontal: one.horizontal, vertical: one.vertical })}
              >
                {one.label}
              </button>
            ))}
          </div>

          <div className="pnode__chips pnode__chips--grid anode__distances">
            {DISTANCES.map((one) => (
              <button
                key={one.label}
                type="button"
                className={`pnode__chip nodrag${Math.abs(one.zoom - zoom) < 1.5 ? ' pnode__chip--on' : ''}`}
                onClick={() => edit({ zoom: one.zoom })}
              >
                {one.label}
              </button>
            ))}
          </div>

          {card.error ? <p className="pnode__error">{card.error}</p> : null}
        </div>
      ) : null}

      {isOpen || mine > 0 ? (
        <button
          type="button"
          className="cnode__run pnode__run nodrag"
          onClick={() => {
            setStarting(true)
            card.onRun?.()
          }}
          disabled={starting || full || !wired}
          title={
            full
              ? `${live} already going, here or elsewhere — wait for one to finish`
              : !wired
                ? 'Connect a picture first'
                : 'See the picture from here'
          }
        >
          {starting ? (
            'Starting…'
          ) : full ? (
            'Queued — ' + live + ' already going'
          ) : (
            <>
              Run
              <span className="pnode__credits">about {ANGLE_CREDITS} credits</span>
              {mine > 0 ? <span className="pnode__credits">· {mine} going</span> : null}
            </>
          )}
        </button>
      ) : null}

      {mine === 0 && card.credits ? (
        <span className="pnode__spent">
          {card.madeCount ?? 0} made · {card.credits} credits
        </span>
      ) : null}

      {!isOpen && card.error ? <span className="cnode__error">{card.error}</span> : null}
    </div>
  )
})
